import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import GuapLogo from './GuapLogo';

const VKIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.862-.525-2.049-1.714-1.032-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.559c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4.03 8.57 4.03 8.096c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.677.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.762-.491h1.744c.525 0 .643.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
  </svg>
);

const Footer = () => {
  const { t } = useLang();

  return (
    <footer style={{
      background: 'rgba(8, 8, 18, 0.98)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      padding: '60px 24px 32px',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '48px',
          marginBottom: '48px',
        }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <GuapLogo size={36} />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>ГУАП</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '2px' }}>SUAI</div>
              </div>
            </div>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: '20px' }}>
              {t('footer_desc')}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* VK */}
              <a
                href="https://vk.com/guap_spb"
                target="_blank"
                rel="noreferrer"
                style={{
                  width: '40px', height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = 'rgba(46,90,255,0.2)';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderColor = '#2E5AFF';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }}
              >
                <VKIcon />
              </a>
              {/* Telegram */}
              <a
                href="https://t.me/guap_official"
                target="_blank"
                rel="noreferrer"
                style={{
                  width: '40px', height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = 'rgba(46,90,255,0.2)';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderColor = '#2E5AFF';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }}
              >
                <Send size={18} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '2px', marginBottom: '20px', textTransform: 'uppercase' }}>
              {t('footer_nav')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { to: '/', label: t('nav_home') },
                { to: '/program', label: t('nav_program') },
                { to: '/project-map', label: t('nav_map') },
                { to: '/tasks', label: t('nav_tasks') },
                { to: '/contacts', label: t('nav_contacts') },
              ].map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    textDecoration: 'none',
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.5)',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseOver={e => e.currentTarget.style.color = '#fff'}
                  onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Standards */}
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '2px', marginBottom: '20px', textTransform: 'uppercase' }}>
              {t('footer_standards')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {['ГОСТ Р 54869-2011', 'ГОСТ Р 54870-2011', 'ГОСТ Р 54871-2011', 'ИСО 21500:2012'].map(s => (
                <div key={s} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>{s}</div>
              ))}
            </div>
          </div>

          {/* Contacts */}
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '2px', marginBottom: '20px', textTransform: 'uppercase' }}>
              {t('nav_contacts')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: <MapPin size={14} />, text: 'ул. Большая Морская, д. 67' },
                { icon: <Mail size={14} />, text: 'info@guap.ru' },
                { icon: <Phone size={14} />, text: '+7 (812) 494-70-00' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
                  <span style={{ marginTop: '1px', flexShrink: 0 }}>{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          paddingTop: '24px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
            {t('footer_copyright')}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'rgba(255,255,255,0.2)',
          }}>
            Учись. Изобретай. Живи.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
